import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import type { AvatarConfig, AvatarType } from '@/lib/avatar-config';
import type { Database } from '@/integrations/supabase/types';

type DbUserProfile = Database['public']['Tables']['user_profiles']['Row'];

interface UserProfile {
  id: string;
  username: string | null;
  display_name: string | null;
  avatar_type: AvatarType;
  avatar_config: AvatarConfig | null;
  avatar_url: string | null;
  elo_rating: number;
  games_played: number;
  games_won: number;
  games_lost: number;
  games_drawn: number;
  location_sharing_consent: boolean | null;
  location_consent_updated_at: string | null;
}

// Convert database row to typed profile
const convertDbProfile = (dbProfile: DbUserProfile): UserProfile => ({
  id: dbProfile.id,
  username: dbProfile.username,
  display_name: dbProfile.display_name,
  avatar_type: (dbProfile.avatar_type as AvatarType) || 'generated',
  avatar_config: dbProfile.avatar_config ? (dbProfile.avatar_config as unknown as AvatarConfig) : null,
  avatar_url: dbProfile.avatar_url,
  elo_rating: dbProfile.elo_rating,
  games_played: dbProfile.games_played,
  games_won: dbProfile.games_won,
  games_lost: dbProfile.games_lost,
  games_drawn: dbProfile.games_drawn,
  location_sharing_consent: (dbProfile as any).location_sharing_consent ?? null,
  location_consent_updated_at: (dbProfile as any).location_consent_updated_at ?? null,
});

export const useUserProfile = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async () => {
    if (!user) {
      setProfile(null);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching profile:', error);
        return;
      }

      setProfile(data ? convertDbProfile(data) : null);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (updates: Partial<Omit<DbUserProfile, 'id' | 'created_at' | 'updated_at'>>) => {
    if (!user || !profile) return { error: 'No user or profile' };

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single();

      if (error) {
        return { error: error.message };
      }

      setProfile(data ? convertDbProfile(data) : null);
      return { data: data ? convertDbProfile(data) : null };
    } catch (error) {
      return { error: error instanceof Error ? error.message : 'Unknown error' };
    }
  };

  const updateAvatar = async (avatarType: AvatarType, avatarConfig: AvatarConfig, avatarUrl?: string) => {
    return updateProfile({
      avatar_type: avatarType as string,
      avatar_config: avatarConfig as any,
      avatar_url: avatarUrl || null
    });
  };

  const updateUsername = async (username: string) => {
    // Check if username is available
    const { data: existingUser } = await supabase
      .from('user_profiles')
      .select('id')
      .eq('username', username)
      .neq('id', user?.id)
      .single();

    if (existingUser) {
      return { error: 'Username is already taken' };
    }

    return updateProfile({ username });
  };

  useEffect(() => {
    fetchProfile();
  }, [user]);

  return {
    profile,
    loading,
    updateProfile,
    updateAvatar,
    updateUsername,
    refreshProfile: fetchProfile
  };
};